/**
 * Song library — save/load songs to localStorage.
 *
 * Storage key: 'chorddetector_library'
 * Format:     [{ id, title, savedAt, lyrics, alignment, songKey, bpm, beatsPerMeasure, transposition, capo }]
 */

import { apiJson } from './apiClient';

const STORAGE_KEY = 'chorddetector_library';

export function loadLibrary() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export async function loadLibraryRemote() {
  const data = await apiJson('/songs');
  return data.songs || [];
}

function buildSongEntry({ title, lyrics, alignment, timedLyrics, songKey, bpm, beatsPerMeasure, transposition, capo }) {
  return {
    title: title || 'Untitled Song',
    lyrics,
    alignment,
    timedLyrics: timedLyrics || [],
    songKey,
    bpm,
    beatsPerMeasure: beatsPerMeasure || null,
    transposition,
    capo,
  };
}

export async function saveSongRemote(song) {
  const data = await apiJson('/songs', {
    method: 'POST',
    body: JSON.stringify(buildSongEntry(song)),
  });
  return data.song;
}

export function saveSongLocal({ title, lyrics, alignment, timedLyrics, songKey, bpm, beatsPerMeasure, transposition, capo }) {
  const library = loadLibrary();
  // Deduplicate by title (update in place)
  const idx = library.findIndex(s => s.title === title);
  const entry = {
    id: idx >= 0 ? library[idx].id : Date.now().toString(36) + Math.random().toString(36).slice(2),
    title: title || 'Untitled Song',
    savedAt: new Date().toISOString(),
    lyrics,
    alignment,
    timedLyrics: timedLyrics || [],
    songKey,
    bpm,
    beatsPerMeasure: beatsPerMeasure || null,
    transposition,
    capo,
  };
  if (idx >= 0) {
    library[idx] = entry;
  } else {
    library.unshift(entry);
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(library));
  } catch (e) {
    if (!(e instanceof DOMException && e.name === 'QuotaExceededError')) throw e;
    // Storage quota exceeded — remove the oldest entry and retry once.
    if (library.length > 1) {
      library.pop();
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(library));
      } catch {
        return null; // Signal to caller that the save failed even after eviction
      }
    } else {
      return null;
    }
  }
  return entry;
}

export async function deleteSongRemote(id) {
  const data = await apiJson(`/songs/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return data.songs || [];
}

export function deleteSongLocal(id) {
  const library = loadLibrary().filter(s => s.id !== id);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(library)); } catch {}
  return library;
}

export async function importLibraryRemote(songs) {
  const data = await apiJson('/songs/import', {
    method: 'POST',
    body: JSON.stringify({ songs }),
  });
  return data.songs || [];
}

export async function migrateLocalLibraryToServer() {
  const local = loadLibrary();
  if (!local.length) return null;
  const songs = await importLibraryRemote(local);
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  return songs;
}

export function exportLibraryFile(library) {
  const json = JSON.stringify(library, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'chorddetector_library.json';
  a.click();
  URL.revokeObjectURL(url);
}
